import {
  PendingWithdrawalRow,
  usePendingWithdrawals,
  WithdrawalStatus,
} from '@/app/components/PortfolioPageComponents/hooks/usePendingWithdrawals';
import PendingWithdrawalWithdrawButton from '@/app/components/PortfolioPageComponents/PendingWithdrawalWithdrawButton';
import RedeemMzusdTable from '@/app/components/PortfolioPageComponents/RedeemMzusdControls';
import { cn } from '@/app/shared/lib/cn';
import { Button } from '@/app/shared/ui/Button';
import DataTable, {
  DataTableColumn,
  DataTableStacked,
  DataTableValue,
} from '@/app/shared/ui/DataTable.tsx';
import { Skeleton } from '@/app/shared/ui/Skeleton';

const STATUS_CLASS = {
  Claimable: 'text-brand-1000',
  Unlocking: 'text-status-unlocking',
};

const STATUS_DOT_CLASS = {
  Claimable: 'bg-brand-1000',
  Unlocking: 'bg-status-unlocking',
};

const desktopGridClassName =
  'min-[960px]:grid min-[960px]:w-full min-[960px]:min-w-0 min-[960px]:grid-cols-[minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1fr)_auto] min-[960px]:items-center min-[960px]:gap-2 min-[960px]:px-5 xl:gap-4';

const withdrawButtonClass = 'h-9 shrink-0 px-4 text-sm';

const StatusCell = ({ status }: { status: WithdrawalStatus }) => (
  <div className='flex min-w-0 items-center gap-2'>
    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT_CLASS[status])} />
    <span className={cn('truncate text-base', STATUS_CLASS[status])}>{status}</span>
  </div>
);

const WITHDRAWALS_COLUMNS: DataTableColumn<PendingWithdrawalRow>[] = [
  {
    id: 'tranche',
    header: 'Tranche',
    cell: (row) => <DataTableStacked primary={row.name} secondary={row.tranche} />,
  },
  {
    id: 'amount',
    header: 'Amount',
    cell: (row) => <DataTableStacked primary={row.amount} secondary={row.amountUsd} />,
  },
  {
    id: 'shares',
    header: 'Shares',
    cell: (row) => <DataTableValue value={row.shares} />,
  },
  {
    id: 'unlockEta',
    header: 'Unlock ETA',
    cell: (row) => <DataTableValue value={row.unlockEta} />,
  },
  {
    id: 'status',
    header: 'Status',
    cell: (row) => <StatusCell status={row.status} />,
  },
];

const PortfolioWithdrawalsTable = () => {
  const { rows, count, isLoading, isConnected } = usePendingWithdrawals();

  return (
    <div className='flex min-w-0 flex-col gap-10'>
      <div className='flex flex-col gap-3'>
        <h2 className='text-base font-normal text-brand-1100'>Pending withdrawals</h2>
        <div className='flex flex-col gap-3 min-[960px]:flex-row min-[960px]:items-start min-[960px]:justify-between'>
          <p className='text-base leading-6 text-gray-400'>
            Availability includes the current epoch end and the tranche withdrawal duration.
          </p>
          <span className='shrink-0 text-base leading-6 text-gray-400'>
            {count} {count === 1 ? 'request' : 'requests'}
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className='flex flex-col gap-3'>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton.Line key={index} loading className='h-[74px] w-full rounded-md'>
              <div className='h-[74px]' />
            </Skeleton.Line>
          ))}
        </div>
      ) : !isConnected ? (
        <p className='text-base text-gray-400'>Connect wallet to see pending withdrawals</p>
      ) : (
        <div className='flex flex-col gap-10'>
          <DataTable
            data={rows}
            getRowKey={(row) => row.id}
            desktopGridClassName={desktopGridClassName}
            emptyText='No pending withdrawals'
            pagination={{ desktop: 5, mobile: 2 }}
            desktopRowClassName='h-[74px] py-4'
            mobileRowClassName='p-[12px_16px]'
            headerTrailing={
              <Button
                variant='outline'
                className={cn(withdrawButtonClass, 'pointer-events-none invisible')}
                tabIndex={-1}
                aria-hidden
              >
                Withdraw
              </Button>
            }
            columns={WITHDRAWALS_COLUMNS}
            rowTrailing={(row) => <PendingWithdrawalWithdrawButton row={row} />}
            renderMobile={(row) => (
              <div className='flex flex-col gap-[21px]'>
                <div className='flex items-start justify-between gap-4'>
                  <div className='flex min-w-0 flex-col gap-0.5'>
                    <span className='text-base text-gray-200'>{row.name}</span>
                    <span className='text-sm text-gray-400'>{row.tranche}</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT_CLASS[row.status])} />
                    <span className={cn('text-base', STATUS_CLASS[row.status])}>{row.status}</span>
                  </div>
                </div>

                <div className='flex min-w-0 flex-col gap-0.5'>
                  <span className='text-sm font-medium tracking-[0.075em] text-gray-500'>Amount</span>
                  <DataTableStacked primary={row.amount} secondary={row.amountUsd} />
                </div>

                <div className='grid grid-cols-2 gap-x-4'>
                  <div className='flex min-w-0 flex-col gap-2'>
                    <span className='text-sm font-medium tracking-[0.075em] text-gray-500'>Shares</span>
                    <DataTableValue value={row.shares} />
                  </div>
                  <div className='flex min-w-0 flex-col gap-2'>
                    <span className='text-sm font-medium tracking-[0.075em] text-gray-500'>Unlock ETA</span>
                    <DataTableValue value={row.unlockEta} />
                  </div>
                </div>

                <PendingWithdrawalWithdrawButton row={row} className='w-full' />
              </div>
            )}
          />

          <RedeemMzusdTable />
        </div>
      )}
    </div>
  );
};

export default PortfolioWithdrawalsTable;
